import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { HelpTooltip } from "@/components/HelpTooltip";
import {
  Download,
  TrendingUp,
  ArrowUpRight,
  ArrowDownLeft,
  ShoppingCart,
  Clock,
  Users,
  Activity,
  ChevronLeft
} from "lucide-react";
import { router } from '@inertiajs/react';
import AppLayout from '@/layouts/AppLayout';
import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

export default function FinancialHistoryReport({
  timeline,
  summary,
  dailyMovements,
  mostActive,
  allUsers,
  selectedUser,
  filters,
  dateRange
}) {
  const [selectedRange, setSelectedRange] = useState(filters.range);
  const [selectedUserId, setSelectedUserId] = useState(filters.user_id);
  const [customDateRange, setCustomDateRange] = useState({
    start_date: filters.start_date || '',
    end_date: filters.end_date || ''
  });

  const handleBackToReports = () => {
    router.visit("/admin?tab=reports");
  };

  const handleFilterChange = (newRange, newUserId = selectedUserId) => {
    const params = new URLSearchParams();
    params.set('range', newRange);
    if (newUserId) params.set('user_id', newUserId);
    router.visit(`/admin/reports/financial_history?${params.toString()}`);
  };

  const handleApplyCustomDates = () => {
    if (customDateRange.start_date && customDateRange.end_date) {
      const params = new URLSearchParams();
      params.set('range', 'custom');
      params.set('start_date', customDateRange.start_date);
      params.set('end_date', customDateRange.end_date);
      if (selectedUserId) params.set('user_id', selectedUserId);
      router.visit(`/admin/reports/financial_history?${params.toString()}`);
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    params.set('range', selectedRange);
    if (selectedUserId) params.set('user_id', selectedUserId);
    window.location.href = `/admin/reports/financial_history/export.csv?${params.toString()}`;
  };

  const getMovementIcon = (type) => {
    const icons = {
      'recognition_sent': <ArrowUpRight className="w-5 h-5 text-orange-500" />,
      'recognition_received': <ArrowDownLeft className="w-5 h-5 text-green-500" />,
      'redemption': <ShoppingCart className="w-5 h-5 text-red-500" />,
      'admin_grant': <TrendingUp className="w-5 h-5 text-blue-500" />
    };
    return icons[type] || <Activity className="w-5 h-5 text-gray-500" />;
  };

  const getMovementColor = (points) => {
    if (points > 0) return 'text-green-600';
    if (points < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const formatPoints = (points) => {
    const prefix = points > 0 ? '+' : '';
    return `${prefix}${points.toLocaleString()} pts`;
  };

  return (
    <div className="space-y-6 pb-20">
      <Button
        onClick={handleBackToReports}
        variant="ghost"
        className="mb-4 hover:bg-gray-100"
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        Volver a Reportes
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Clock className="w-8 h-8 text-purple-600" />
            Historial Financiero
            <HelpTooltip content={
              <div>
                <p className="font-semibold mb-1">¿Qué es este reporte?</p>
                <p className="mb-2">Provee un timeline detallado de todos los movimientos de puntos en el sistema, útil para auditoría y resolución de discrepancias.</p>
                <p className="font-semibold mb-1">Verás:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Timeline completo de movimientos</li>
                  <li>Resumen: enviado/recibido/canjeado</li>
                  <li>Movimientos agregados por día</li>
                  <li>Usuarios más activos</li>
                  <li>Filtro por usuario específico</li>
                </ul>
              </div>
            } />
          </h1>
          <p className="text-gray-500 mt-1">
            Timeline completo de movimientos de puntos
            {selectedUser && ` - ${selectedUser.full_name}`}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Período: {dateRange.start} a {dateRange.end}
          </p>
        </div>
        <Button
          onClick={handleExport}
          variant="outline"
          className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Período
              </label>
              <select
                value={selectedRange}
                onChange={(e) => {
                  setSelectedRange(e.target.value);
                  if (e.target.value !== 'custom') {
                    handleFilterChange(e.target.value);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="today">Hoy</option>
                <option value="yesterday">Ayer</option>
                <option value="last_7_days">Últimos 7 días</option>
                <option value="last_30_days">Últimos 30 días</option>
                <option value="last_90_days">Últimos 90 días</option>
                <option value="this_month">Este mes</option>
                <option value="last_month">Mes pasado</option>
                <option value="this_year">Este año</option>
                <option value="custom">Rango personalizado...</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usuario (opcional)
              </label>
              <select
                value={selectedUserId || ''}
                onChange={(e) => {
                  const newUserId = e.target.value || null;
                  setSelectedUserId(newUserId);
                  handleFilterChange(selectedRange, newUserId);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Todos los usuarios</option>
                {allUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Rango de fechas personalizado */}
          {selectedRange === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Inicio</label>
                <input
                  type="date"
                  value={customDateRange.start_date}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin</label>
                <input
                  type="date"
                  value={customDateRange.end_date}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleApplyCustomDates}
                  disabled={!customDateRange.start_date || !customDateRange.end_date}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Aplicar Fechas
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Reconocimientos Recibidos</p>
                <p className="text-2xl font-bold text-green-600">
                  +{summary.recognitions_received.total_points.toLocaleString()} pts
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {summary.recognitions_received.count} movimientos
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <ArrowDownLeft className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Reconocimientos Enviados</p>
                <p className="text-2xl font-bold text-orange-600">
                  -{summary.recognitions_sent.total_points.toLocaleString()} pts
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {summary.recognitions_sent.count} movimientos
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Canjes</p>
                <p className="text-2xl font-bold text-red-600">
                  -{summary.redemptions.total_points.toLocaleString()} pts
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {summary.redemptions.count} movimientos
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Ajustes Admin</p>
                <p className="text-2xl font-bold text-blue-600">
                  {summary.admin_adjustments.total_points >= 0 ? '+' : ''}
                  {summary.admin_adjustments.total_points.toLocaleString()} pts
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {summary.admin_adjustments.count} movimientos
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Movimientos Diarios</CardTitle>
          <p className="text-sm text-gray-500">Balance neto por día</p>
        </CardHeader>
        <CardContent>
          {dailyMovements.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400">
              No hay datos para mostrar
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyMovements}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                  style={{ fontSize: '11px' }}
                />
                <YAxis style={{ fontSize: '11px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString('es-ES')}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line type="monotone" dataKey="received" stroke="#10b981" name="Recibidos" strokeWidth={2} />
                <Line type="monotone" dataKey="sent" stroke="#f59e0b" name="Enviados" strokeWidth={2} />
                <Line type="monotone" dataKey="redeemed" stroke="#ef4444" name="Canjeados" strokeWidth={2} />
                <Line type="monotone" dataKey="net" stroke="#8b5cf6" name="Neto" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-500" />
            Timeline de Movimientos ({timeline.length})
          </CardTitle>
          <p className="text-sm text-gray-500">Historial detallado de transacciones</p>
        </CardHeader>
        <CardContent>
          {timeline.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No hay movimientos en este período
            </div>
          ) : (
            <div className="space-y-4">
              {timeline.map((movement, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-shrink-0 mt-1">
                    {getMovementIcon(movement.type)}
                  </div>

                  <div className="flex-grow min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-800">
                          {movement.icon} {movement.label}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {movement.description}
                        </p>
                        {movement.related_user && (
                          <p className="text-xs text-gray-500 mt-1">
                            {movement.type === 'recognition_sent' ? 'Para: ' :
                             movement.type === 'recognition_received' ? 'De: ' :
                             'Por: '}
                            {movement.related_user}
                          </p>
                        )}
                      </div>

                      <div className="flex-shrink-0 text-right">
                        <p className={`font-bold text-lg ${getMovementColor(movement.points)}`}>
                          {formatPoints(movement.points)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(movement.created_at).toLocaleString('es-ES', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>

                    {movement.metadata?.hashtag && (
                      <Badge variant="outline" className="mt-2 text-xs">
                        {movement.metadata.hashtag}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {mostActive.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Usuarios Más Activos
            </CardTitle>
            <p className="text-sm text-gray-500">Top 10 usuarios por cantidad de movimientos</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="pb-3 text-sm font-medium text-gray-600">#</th>
                    <th className="pb-3 text-sm font-medium text-gray-600">Usuario</th>
                    <th className="pb-3 text-sm font-medium text-gray-600">Departamento</th>
                    <th className="pb-3 text-sm font-medium text-gray-600 text-center">Enviados</th>
                    <th className="pb-3 text-sm font-medium text-gray-600 text-center">Recibidos</th>
                    <th className="pb-3 text-sm font-medium text-gray-600 text-center">Canjes</th>
                    <th className="pb-3 text-sm font-medium text-gray-600 text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {mostActive.map((user, idx) => (
                    <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 text-sm text-gray-600">{idx + 1}</td>
                      <td className="py-3">
                        <p className="text-sm font-medium text-gray-800">{user.full_name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </td>
                      <td className="py-3 text-sm text-gray-600">
                        {user.department?.name}
                      </td>
                      <td className="py-3 text-center">
                        <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                          {user.sent_count}
                        </Badge>
                      </td>
                      <td className="py-3 text-center">
                        <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
                          {user.received_count}
                        </Badge>
                      </td>
                      <td className="py-3 text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                          {user.redemption_count}
                        </Badge>
                      </td>
                      <td className="py-3 text-center">
                        <Badge className="bg-purple-100 text-purple-700">
                          {user.total_movements}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

FinancialHistoryReport.layout = page => <AppLayout children={page} />;
